import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Modal,
  TextInput,
  Button,
  Stack,
  Text,
  Group,
  Loader,
  Badge,
} from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { Hostname } from '@lib2/model/common/ServiceHosts';
import {
  APP_CONSTRAINTS,
  APP_WEB_SHARED_DOMAIN_ID,
  APP_WEB_DOMAIN_LABELS,
} from '@/lib/constants/app';
import { useCheckCustomNameAvailability } from '../useAppMutations';
import { appWebCreateFormSchema } from '../schemas/AppWebCreateForm.schema';
import type { AppWebCreateFormSchemaData } from '../schemas/AppWebCreateForm.schema';

interface AppWebCreateDialogProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (customName: string, sharedDomainId: number) => void;
  isPending: boolean;
}

function getDefaultSharedDomainId(): number {
  return Hostname.isResellerHostname()
    ? APP_WEB_SHARED_DOMAIN_ID.CloudsiteDotApp
    : APP_WEB_SHARED_DOMAIN_ID.VideositeDotApp;
}

export function AppWebCreateDialog({
  opened,
  onClose,
  onSubmit,
  isPending,
}: AppWebCreateDialogProps) {
  const defaultDomainId = getDefaultSharedDomainId();
  const domainLabel = APP_WEB_DOMAIN_LABELS[defaultDomainId];

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<AppWebCreateFormSchemaData>({
    resolver: zodResolver(appWebCreateFormSchema),
    defaultValues: {
      customName: '',
      sharedDomainId: defaultDomainId,
    },
  });

  const customNameValue = watch('customName');

  // Live availability check (debounced via staleTime in the hook)
  const {
    data: availResult,
    isLoading: isChecking,
    isFetched,
  } = useCheckCustomNameAvailability(customNameValue);

  const isAvailable = availResult?.isAvailable ?? false;
  const showAvailability = customNameValue.trim().length >= 2 && isFetched;

  // Reset form when dialog opens
  useEffect(() => {
    if (opened) {
      reset({ customName: '', sharedDomainId: defaultDomainId });
    }
  }, [opened, reset, defaultDomainId]);

  const handleFormSubmit = (data: AppWebCreateFormSchemaData) => {
    onSubmit(data.customName, data.sharedDomainId);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const previewDomain = customNameValue.trim()
    ? `${customNameValue.trim().toLowerCase()}.${domainLabel}`
    : `your-subdomain.${domainLabel}`;

  return (
    <Modal opened={opened} onClose={handleClose} title="Enable Web App" size="md">
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack gap="md">
          <Controller
            name="customName"
            control={control}
            render={({ field }) => (
              <TextInput
                label="Subdomain"
                placeholder="my-app"
                description={`${field.value.length}/${APP_CONSTRAINTS.CUSTOM_NAME_MAX} — Lowercase letters, numbers, and hyphens`}
                required
                maxLength={APP_CONSTRAINTS.CUSTOM_NAME_MAX}
                error={errors.customName?.message}
                rightSection={
                  isChecking ? (
                    <Loader size={14} />
                  ) : showAvailability ? (
                    isAvailable ? (
                      <IconCheck size={14} color="green" />
                    ) : (
                      <IconX size={14} color="red" />
                    )
                  ) : null
                }
                {...field}
                onChange={(e) => {
                  const sanitized = e.currentTarget.value
                    .toLowerCase()
                    .replace(/[^a-z0-9-]/g, '') // strip invalid chars
                    .replace(/^-+/, '')          // no leading hyphens
                    .replace(/-{2,}/g, '-');     // collapse consecutive hyphens
                  field.onChange(sanitized);
                }}
              />
            )}
          />

          {showAvailability && !isAvailable && (
            <Text size="sm" c="red">
              This subdomain is already taken. Please choose another.
            </Text>
          )}

          {/* Domain preview */}
          <Group gap="xs">
            <Text size="sm" c="dimmed">
              Your web app will be available at:
            </Text>
            <Badge variant="light" color="blue" size="lg">
              {previewDomain}
            </Badge>
          </Group>

          <Button
            type="submit"
            loading={isPending}
            disabled={!isAvailable || !customNameValue.trim()}
            fullWidth
          >
            Enable Web App
          </Button>
        </Stack>
      </form>
    </Modal>
  );
}
